import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/config/site";

const CONTENT = {
  privacy: {
    path: "/privacy-policy",
    seoTitle: "Privacy Policy | NRI Property Services Hyderabad",
    seoDescription: "How NRI Property Services collects, uses and protects your personal information.",
    title: ["Privacy Policy"],
    sections: [
      {
        h: "What we collect",
        p: `When you submit an enquiry, we collect the details you provide: your name, country of residence, WhatsApp number, email address and information about your property and requirements. We do not collect passwords, OTPs, Aadhaar numbers, bank credentials or other sensitive identity information through this website.`,
      },
      {
        h: "How we use it",
        p: "Your information is used solely to respond to your enquiry, prepare a property assessment or proposal, and communicate with you about the services you requested. We do not sell, rent or share your personal information with third parties for marketing purposes.",
      },
      {
        h: "How we store it",
        p: "Enquiry data is stored securely on our systems and accessible only to authorised team members handling your request. You may ask us to update or delete your information at any time by writing to us.",
      },
      {
        h: "Analytics",
        p: "This website may use privacy-conscious analytics (such as Google Analytics) to understand aggregate usage and improve the site. No analytics tool is used to collect sensitive personal information.",
      },
      {
        h: "Contact",
        p: `For any privacy question, contact us at ${SITE.email} or on WhatsApp at ${SITE.whatsappDisplay}.`,
      },
    ],
  },
  terms: {
    path: "/terms",
    seoTitle: "Terms & Conditions | NRI Property Services Hyderabad",
    seoDescription: "Terms and conditions for using the NRI Property Services website and engaging our property management services.",
    title: ["Terms & Conditions"],
    sections: [
      {
        h: "Services",
        p: `${SITE.name} (${SITE.domain}) provides property management, property care and local owner representation services in Hyderabad and Greater Hyderabad. The specific scope, charges and responsibilities for any engagement are defined in a written proposal agreed with the owner before work begins.`,
      },
      {
        h: "Owner approval",
        p: "Major expenses and works require owner approval wherever practical. In genuine emergency situations where immediate action is necessary to prevent further damage to the property, we may act first and inform the owner immediately.",
      },
      {
        h: "Website content",
        p: "The content on this website is provided for general information. It does not constitute a binding offer, tax advice, legal advice or financial advice. Service availability and pricing are confirmed individually.",
      },
      {
        h: "Third-party providers",
        p: "Certain works are executed by independent vendors and service providers coordinated by us. While we supervise and document their work, contractual terms for specialised services may involve the provider directly.",
      },
      {
        h: "Contact",
        p: `Questions about these terms: ${SITE.email}.`,
      },
    ],
  },
  disclaimer: {
    path: "/disclaimer",
    seoTitle: "Disclaimer | NRI Property Services Hyderabad",
    seoDescription: "Important disclaimers regarding the services and information provided by NRI Property Services.",
    title: ["Disclaimer"],
    sections: [
      {
        h: "Not tax, legal or financial advice",
        p: "Nothing on this website — including the NRI Tax & TDS guide — constitutes tax, legal, financial or investment advice. Tax, TDS, FEMA, repatriation and remittance requirements depend on your individual circumstances and may change. Always consult your Chartered Accountant, bank or legal advisor.",
      },
      {
        h: "Scope of representation",
        p: "We act as a property management and coordination service. We are not a law firm, tax consultancy, or government body, and we do not provide services that require specific statutory licensing unless explicitly agreed in writing.",
      },
      {
        h: "Accuracy of information",
        p: "We work to keep this website accurate and current, but make no warranties about completeness or applicability to your specific situation. Illustrations of dashboards and reports on this site are conceptual previews of our reporting approach.",
      },
      {
        h: "Service area",
        p: "Our services are currently available in Hyderabad and Greater Hyderabad only.",
      },
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind] || CONTENT.privacy;
  return (
    <>
      <SEO title={c.seoTitle} description={c.seoDescription} path={c.path} />
      <PageHero eyebrow={SITE.name} titleLines={c.title} copy={`Last updated: 2026 · ${SITE.location}`} />
      <section className="bg-ivory py-20 sm:py-28">
        <div className="container-site max-w-3xl space-y-12">
          {c.sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.05}>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy-900">
                <span className="mr-3 text-gold-500/70">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{s.p}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
